import React, { useEffect, useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, DollarSignIcon, ShoppingCartIcon, EyeIcon, ClipboardListIcon, XIcon, PackageIcon, UserIcon, FilterIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, BanIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Visit } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { supabaseAdmin } from '../lib/supabase';
export function VisitsPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  // ── Retroactive discount map (same logic as CollectionsPage) ──────────────
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    const computeRetroDiscounts = async () => {
      // Find cobro visits where descuento === 0 but may have PP% on invoices
      const cobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0 && (v.cobro.descuento || 0) === 0 && !(v.cobro as any)?.anulado);
      if (cobroVisits.length === 0) {
        setRetroDiscountMap({});
        return;
      }
      // Build map of visitId → invoice numbers
      const visitFactMap: Record<string, string[]> = {};
      const allNums: string[] = [];
      cobroVisits.forEach(v => {
        const fd: any[] = (v.cobro as any).facturasDetalle || [];
        const nums = fd.length > 0 ? fd.map((f: any) => f.numeroFactura).filter(Boolean) : v.cobro.numeroFactura ? v.cobro.numeroFactura.split(',').map((n: string) => n.trim()).filter(Boolean) : [];
        if (nums.length > 0) {
          visitFactMap[v.id] = nums;
          allNums.push(...nums);
        }
      });
      const uniqueNums = [...new Set(allNums)];
      if (uniqueNums.length === 0) return;
      try {
        const {
          data: invRows
        } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
        if (!invRows) return;
        const ppMap: Record<string, {
          ppPct: number | null;
          subTotal: number;
        }> = {};
        invRows.forEach((row: any) => {
          ppMap[row.numero_factura] = {
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
            subTotal: Number(row.sub_total) || 0
          };
        });
        const newMap: Record<string, number> = {};
        Object.entries(visitFactMap).forEach(([visitId, nums]) => {
          const v = visits.find(vis => vis.id === visitId);
          if (!v) return;
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          let totalDisc = 0;
          nums.forEach(num => {
            const pp = ppMap[num];
            const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
            if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
              totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
            }
          });
          if (totalDisc > 0) newMap[visitId] = totalDisc;
        });
        setRetroDiscountMap(newMap);
      } catch {
        // Silently fail
      }
    };
    computeRetroDiscounts();
  }, [visits]);
  // Helper: get effective cobro amount (accounting for discount)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      // Discount was saved at visit time — montoCobrado should already be after discount
      // but if montoCobrado equals credito, it wasn't adjusted, so subtract
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        // montoCobrado was NOT adjusted — subtract descuento
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    // Check retroactive discount
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  // Helper: get total discount for a visit (saved or retroactive)
  const getVisitDiscount = (visit: Visit): number => {
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) return savedDesc;
    return retroDiscountMap[visit.id] || 0;
  };
  // Get active vendors for filter (non-admin users)
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'en_progreso',
    label: 'En Progreso'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }];
  const typeOptions = [{
    value: '',
    label: 'Todos los tipos'
  }, {
    value: 'cobro',
    label: 'Cobro'
  }, {
    value: 'venta',
    label: 'Venta'
  }, {
    value: 'cobro_y_venta',
    label: 'Cobro y Venta'
  }, {
    value: 'seguimiento',
    label: 'Seguimiento'
  }];
  const filteredVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (statusFilter && v.estado !== statusFilter) return false;
    if (vendorFilter && v.vendedorId !== vendorFilter) return false;
    if (typeFilter && v.tipoVisita !== typeFilter) return false;
    return true;
  });
  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    vendedor: v => v.vendedorNombre,
    cliente: v => v.clienteNombre,
    tipo: v => v.tipoVisita,
    cobro: v => v.cobro.montoCobrado,
    venta: v => v.venta.montoVenta,
    tiempo: v => v.tiempoTotal || 0,
    estado: v => v.estado
  });
  // Default sort by date descending if no sort config
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // FIXED: Use Dominican Republic locale and currency
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const getVisitTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'Cobro y Venta',
      seguimiento: 'Seguimiento'
    };
    return labels[type] || type;
  };
  const getVisitTypeBadge = (type: string) => {
    const config: Record<string, {
      bg: string;
      text: string;
    }> = {
      cobro: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700'
      },
      venta: {
        bg: 'bg-blue-100',
        text: 'text-blue-700'
      },
      cobro_y_venta: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-700'
      },
      seguimiento: {
        bg: 'bg-amber-100',
        text: 'text-amber-700'
      }
    };
    const style = config[type] || {
      bg: 'bg-slate-100',
      text: 'text-slate-700'
    };
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.text}`} data-id="element-4020">
        {getVisitTypeLabel(type)}
      </span>;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'completada') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4021">
          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-4022" />
          Completada
        </span>;
    if (status === 'en_progreso') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4023">
          <ClockIcon className="w-3.5 h-3.5" data-id="element-4024" />
          En Progreso
        </span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-4025">
        <XCircleIcon className="w-3.5 h-3.5" data-id="element-4026" />
        Cancelada
      </span>;
  };
  const getInvoiceStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pagada_completa: 'Pagada Completa',
      pago_parcial: 'Pago Parcial',
      no_pago: 'No Pagó'
    };
    return labels[status] || status;
  };
  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return '-';
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cheque: 'Cheque'
    };
    return labels[method] || method;
  };
  // Stats calculations (exclude anulado cobros, account for discounts)
  const totalCobrado = filteredVisits.reduce((sum, v) => {
    if ((v.cobro as any)?.anulado) return sum;
    return sum + getEffectiveCobro(v);
  }, 0);
  const totalVendido = filteredVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const visitasCompletadas = filteredVisits.filter(v => v.estado === 'completada').length;
  const visitasConTiempo = filteredVisits.filter(v => v.tiempoTotal);
  const tiempoPromedio = visitasConTiempo.length > 0 ? Math.round(visitasConTiempo.reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / visitasConTiempo.length) : 0;
  const clearFilters = () => {
    setDateFilter('');
    setStatusFilter('');
    setVendorFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };
  const hasActiveFilters = dateFilter || statusFilter || vendorFilter || typeFilter;
  // Render cobro cell with proper logic (includes anulado check + discount)
  const renderCobroCell = (visit: Visit) => {
    const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneCobroTipo) {
      return <span className="text-slate-300" data-id="element-4027">—</span>;
    }
    // Check if cobro was anulled
    if ((visit.cobro as any)?.anulado) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-4028">
          <BanIcon className="w-3 h-3" data-id="element-4029" /> Anulado
        </span>;
    }
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-4030">
          No cobró
        </span>;
    }
    const effectiveAmount = getEffectiveCobro(visit);
    const discount = getVisitDiscount(visit);
    return <div className="text-right" data-id="element-4031">
        <span className="font-semibold text-emerald-600" data-id="element-4032">
          {formatCurrency(effectiveAmount)}
        </span>
        {discount > 0 && <span className="block text-[10px] text-emerald-500 font-medium" data-id="element-4033">
            PP: -{formatCurrency(discount)}
          </span>}
      </div>;
  };
  // FIXED: Render venta cell with proper logic
  const renderVentaCell = (visit: Visit) => {
    const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneVentaTipo) {
      return <span className="text-slate-300" data-id="element-4034">—</span>;
    }
    // FIXED: Check both huboVenta AND montoVenta > 0
    if (!visit.venta.huboVenta || visit.venta.montoVenta <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600" data-id="element-4035">
          No vendió
        </span>;
    }
    return <span className="font-semibold text-indigo-600" data-id="element-4036">
        {formatCurrency(visit.venta.montoVenta)}
      </span>;
  };
  return <div className="space-y-6" data-id="element-4037">
      <PageHeader title="Historial de Visitas" subtitle={`${filteredVisits.length} registros encontrados`} icon={<MapPinIcon className="w-6 h-6" data-id="element-4039" />} data-id="element-4038" />

      {/* Filters */}
      <Card className="animate-stagger-1" data-id="element-4040">
        <div className="flex items-center gap-2 mb-4" data-id="element-4041">
          <FilterIcon className="w-4 h-4 text-slate-400" data-id="element-4042" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-4043">Filtros</span>
          {hasActiveFilters && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-medium" data-id="element-4044">
              {[dateFilter, statusFilter, vendorFilter, typeFilter].filter(Boolean).length}{' '}
              activos
            </span>}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-4045">
          <div className="col-span-1 min-w-0" data-id="element-4046">
            <Input type="date" value={dateFilter} onChange={e => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-4048" />} label="Fecha" data-id="element-4047" />
          </div>
          {isAdmin && <div className="col-span-1 min-w-0" data-id="element-4049">
              <Select label="Vendedor" options={vendorOptions} value={vendorFilter} onChange={e => {
            setVendorFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4050" />
            </div>}
          <div className="col-span-1 min-w-0" data-id="element-4051">
            <Select label="Tipo de Visita" options={typeOptions} value={typeFilter} onChange={e => {
            setTypeFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4052" />
          </div>
          <div className="col-span-1 min-w-0" data-id="element-4053">
            <Select label="Estado" options={statusOptions} value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-4054" />
          </div>
          <div className="col-span-2 lg:col-span-1 flex items-end" data-id="element-4055">
            <Button variant="secondary" onClick={clearFilters} className="w-full" disabled={!hasActiveFilters} data-id="element-4056">
              Limpiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-4057">
        <StatsCard title="Total Visitas" value={filteredVisits.length} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-4059" />} variant="primary" data-id="element-4058" />
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-4061" />} variant="success" data-id="element-4060" />
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-4063" />} variant="warning" data-id="element-4062" />
        <StatsCard title="Tiempo Promedio" value={`${tiempoPromedio} min`} icon={<ClockIcon className="w-5 h-5" data-id="element-4065" />} variant="default" data-id="element-4064" />
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3 animate-stagger-2" data-id="element-4066">
        {filteredVisits.length === 0 ? <Card data-id="element-4067">
            <div className="p-8 text-center" data-id="element-4068">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-2xl mb-3" data-id="element-4069">
                <ClipboardListIcon className="w-7 h-7 text-slate-400" data-id="element-4070" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1" data-id="element-4071">
                No hay visitas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4072">
                {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
              </p>
            </div>
          </Card> : paginatedVisits.map(visit => {
        const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
        const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
        const effectiveCobro = getEffectiveCobro(visit);
        const discount = getVisitDiscount(visit);
        return <Card key={visit.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedVisit(visit)} data-id="element-4073">
                <div className="flex items-start justify-between gap-3 mb-3" data-id="element-4074">
                  <div className="flex-1 min-w-0" data-id="element-4075">
                    <p className="font-semibold text-slate-900 truncate" data-id="element-4076">
                      {visit.clienteNombre}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-4077">
                      {visit.zona}
                    </p>
                    {isAdmin && <p className="text-xs text-indigo-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-4078">
                        <UserIcon className="w-3 h-3" data-id="element-4079" />
                        {visit.vendedorNombre}
                      </p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-4080">
                    {getStatusBadge(visit.estado)}
                    {getVisitTypeBadge(visit.tipoVisita)}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-4081">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-4082" />
                  <span data-id="element-4083">{formatDate(visit.fecha)}</span>
                  <span data-id="element-4084">•</span>
                  <ClockIcon className="w-3.5 h-3.5" data-id="element-4085" />
                  <span data-id="element-4086">{formatTime(visit.horaLlegada)}</span>
                  {visit.tiempoTotal && <>
                      <span data-id="element-4087">•</span>
                      <span data-id="element-4088">{visit.tiempoTotal} min</span>
                    </>}
                </div>

                <div className="grid grid-cols-2 gap-2" data-id="element-4089">
                  {tieneCobroTipo && <div className={`p-2.5 rounded-xl ${(visit.cobro as any)?.anulado ? 'bg-slate-50' : 'bg-emerald-50'}`} data-id="element-4090">
                      <p className={`text-xs font-medium mb-0.5 ${(visit.cobro as any)?.anulado ? 'text-slate-400' : 'text-emerald-600'}`} data-id="element-4091">
                        Cobro
                      </p>
                      <p className="font-bold" data-id="element-4092">
                        {(visit.cobro as any)?.anulado ? <span className="inline-flex items-center gap-1 text-slate-500 text-sm" data-id="element-4093">
                            <BanIcon className="w-3 h-3" data-id="element-4094" /> Anulado
                          </span> : visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 ? <>
                            <span className="text-emerald-700" data-id="element-4095">
                              {formatCurrency(effectiveCobro)}
                            </span>
                            {discount > 0 && <span className="block text-[10px] text-emerald-500 font-medium mt-0.5" data-id="element-4096">
                                PP: -{formatCurrency(discount)}
                              </span>}
                          </> : <span className="text-rose-500 text-sm" data-id="element-4097">
                            No cobró
                          </span>}
                      </p>
                    </div>}
                  {tieneVentaTipo && <div className="p-2.5 bg-indigo-50 rounded-xl" data-id="element-4098">
                      <p className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-4099">
                        Venta
                      </p>
                      <p className="font-bold text-indigo-700" data-id="element-4100">
                        {visit.venta.huboVenta && visit.venta.montoVenta > 0 ? formatCurrency(visit.venta.montoVenta) : <span className="text-amber-500 text-sm" data-id="element-4101">
                            No vendió
                          </span>}
                      </p>
                    </div>}
                </div>

                {visit.productosVencidos !== null && visit.productosVencidos !== undefined && <div className={`mt-3 flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-lg ${visit.productosVencidos ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-4102">
                      {visit.productosVencidos ? <>
                          <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-4103" />
                          Tiene productos vencidos
                        </> : <>
                          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-4104" />
                          Sin productos vencidos
                        </>}
                    </div>}

                {visit.motivoNoPago && <p className="mt-2 text-xs text-rose-600 bg-rose-50 px-2.5 py-1.5 rounded-lg" data-id="element-4105">
                    ⚠ {visit.motivoNoPago}
                  </p>}
              </Card>;
      })}
      </div>

      {/* Desktop Table */}
      <Card padding="none" className="hidden md:block animate-stagger-2" data-id="element-4106">
        {filteredVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-4107">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4108">
              <ClipboardListIcon className="w-8 h-8 text-slate-400" data-id="element-4109" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4110">
              No hay visitas
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-4111">
              {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-4112">
            <table className="w-full" data-id="element-4113">
              <thead data-id="element-4114">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-4115">
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4116" />
                  {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4117" />}
                  <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4118" />
                  <SortableTableHeader label="Tipo" sortKey="tipo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4119" />
                  <SortableTableHeader label="Cobro" sortKey="cobro" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4120" />
                  <SortableTableHeader label="Venta" sortKey="venta" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4121" />
                  <SortableTableHeader label="Tiempo" sortKey="tiempo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4122" />
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-4123" />
                  <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-4124">
                    Vencidos
                  </th>
                  <th className="px-5 py-4 w-12" data-id="element-4125"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-4126">
                {paginatedVisits.map(visit => <tr key={visit.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-4127">
                    <td className="px-5 py-4" data-id="element-4128">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-4129">
                        {formatDate(visit.fecha)}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-4130">
                        {formatTime(visit.horaLlegada)}
                      </p>
                    </td>
                    {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-4131">
                        {visit.vendedorNombre}
                      </td>}
                    <td className="px-5 py-4" data-id="element-4132">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-4133">
                        {visit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-4134">{visit.zona}</p>
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4135">
                      {getVisitTypeBadge(visit.tipoVisita)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-4136">
                      {renderCobroCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-4137">
                      {renderVentaCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-center text-sm text-slate-600" data-id="element-4138">
                      {visit.tiempoTotal ? `${visit.tiempoTotal} min` : <span className="text-slate-300" data-id="element-4139">—</span>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4140">
                      {getStatusBadge(visit.estado)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-4141">
                      {visit.productosVencidos === true ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-4142">
                          Sí
                        </span> : visit.productosVencidos === false ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600" data-id="element-4143">
                          No
                        </span> : <span className="text-slate-300" data-id="element-4144">—</span>}
                    </td>
                    <td className="px-5 py-4" data-id="element-4145">
                      <Button variant="ghost" size="sm" onClick={e => {
                  e.stopPropagation();
                  setSelectedVisit(visit);
                }} data-id="element-4146">
                        <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-4147" />
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Pagination */}
      {filteredVisits.length > 0 && <div className="animate-stagger-3" data-id="element-4148">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4149" />
        </div>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Visita" size="lg" data-id="element-4150">
        {selectedVisit && <>
            <ModalBody data-id="element-4151">
              <div className="space-y-6" data-id="element-4152">
                <div className="flex items-start justify-between pb-4 border-b border-slate-100" data-id="element-4153">
                  <div data-id="element-4154">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-4155">
                      {selectedVisit.clienteNombre}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-4156">
                      {selectedVisit.zona}
                    </p>
                  </div>
                  {getStatusBadge(selectedVisit.estado)}
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-4157">
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4158">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4159">Vendedor</p>
                    <p className="font-semibold text-slate-900" data-id="element-4160">
                      {selectedVisit.vendedorNombre}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4161">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4162">
                      Tipo de Visita
                    </p>
                    <p className="font-semibold text-slate-900" data-id="element-4163">
                      {getVisitTypeLabel(selectedVisit.tipoVisita)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4164">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4165">Fecha</p>
                    <p className="font-semibold text-slate-900" data-id="element-4166">
                      {formatDate(selectedVisit.fecha)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-4167">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-4168">Horario</p>
                    <p className="font-semibold text-slate-900" data-id="element-4169">
                      {formatTime(selectedVisit.horaLlegada)} -{' '}
                      {formatTime(selectedVisit.horaSalida)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-4 bg-[#eef2fc] rounded-xl" data-id="element-4170">
                  <div className="flex items-center gap-2" data-id="element-4171">
                    <ClockIcon className="w-5 h-5 text-[#2952C4]" data-id="element-4172" />
                    <div data-id="element-4173">
                      <p className="text-xs text-[#2952C4] font-medium" data-id="element-4174">
                        Tiempo Total
                      </p>
                      <p className="font-bold text-[#0D1B46]" data-id="element-4175">
                        {selectedVisit.tiempoTotal || 0} minutos
                      </p>
                    </div>
                  </div>
                  {selectedVisit.ubicacionLlegada && <div className="flex items-center gap-2" data-id="element-4176">
                      <MapPinIcon className="w-5 h-5 text-[#2952C4]" data-id="element-4177" />
                      <div data-id="element-4178">
                        <p className="text-xs text-[#2952C4] font-medium" data-id="element-4179">
                          Ubicación GPS
                        </p>
                        <p className="font-bold text-[#0D1B46]" data-id="element-4180">
                          {selectedVisit.ubicacionLlegada.lat.toFixed(4)},{' '}
                          {selectedVisit.ubicacionLlegada.lng.toFixed(4)}
                        </p>
                      </div>
                    </div>}
                </div>

                {(selectedVisit.tipoVisita === 'cobro' || selectedVisit.tipoVisita === 'cobro_y_venta') && (() => {
              const isAnulado = (selectedVisit.cobro as any)?.anulado;
              const huboCobro = selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0;
              const credito = selectedVisit.cobro.credito || 0;
              const descuento = getVisitDiscount(selectedVisit);
              const effectiveAmount = getEffectiveCobro(selectedVisit);
              const facturasDetalle: any[] = (selectedVisit.cobro as any)?.facturasDetalle || [];
              const bgClass = isAnulado ? 'bg-slate-50 border-slate-200' : huboCobro ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200';
              const titleColor = isAnulado ? 'text-slate-500' : huboCobro ? 'text-emerald-800' : 'text-rose-800';
              return <div className={`p-4 rounded-xl border ${bgClass}`} data-id="element-4181">
                        <h4 className={`font-semibold mb-3 flex items-center gap-2 ${titleColor}`} data-id="element-4182">
                          {isAnulado ? <>
                              <BanIcon className="w-5 h-5" data-id="element-4183" /> Cobro Anulado
                            </> : huboCobro ? <>
                              <DollarSignIcon className="w-5 h-5" data-id="element-4184" /> Cobro
                              Realizado
                            </> : <>
                              <DollarSignIcon className="w-5 h-5" data-id="element-4185" /> Sin Cobro
                            </>}
                        </h4>
                        {isAnulado && huboCobro ? <div className="space-y-3" data-id="element-4186">
                            <div className="p-3 bg-slate-100 rounded-lg border border-slate-200" data-id="element-4187">
                              <p className="text-sm text-slate-500 font-medium flex items-center gap-1.5" data-id="element-4188">
                                <BanIcon className="w-4 h-4" data-id="element-4189" />
                                Este cobro fue anulado. Los montos no se
                                contabilizan en los totales.
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm opacity-60" data-id="element-4190">
                              <div data-id="element-4191">
                                <p className="text-slate-500" data-id="element-4192">Monto Original</p>
                                <p className="font-bold text-slate-600 line-through" data-id="element-4193">
                                  {formatCurrency(selectedVisit.cobro.montoCobrado)}
                                </p>
                              </div>
                              <div data-id="element-4194">
                                <p className="text-slate-500" data-id="element-4195">Método de Pago</p>
                                <p className="font-bold text-slate-600" data-id="element-4196">
                                  {getPaymentMethodLabel(selectedVisit.cobro.metodoPago)}
                                </p>
                              </div>
                            </div>
                          </div> : huboCobro ? <div className="space-y-3" data-id="element-4197">
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-4198">
                              <div data-id="element-4199">
                                <p className="text-emerald-600" data-id="element-4200">
                                  Monto Cobrado
                                </p>
                                <p className="font-bold text-emerald-900" data-id="element-4201">
                                  {formatCurrency(effectiveAmount)}
                                </p>
                              </div>
                              <div data-id="element-4202">
                                <p className="text-emerald-600" data-id="element-4203">
                                  Método de Pago
                                </p>
                                <p className="font-bold text-emerald-900" data-id="element-4204">
                                  {getPaymentMethodLabel(selectedVisit.cobro.metodoPago)}
                                </p>
                              </div>
                              {credito > 0 && <div data-id="element-4205">
                                  <p className="text-emerald-600" data-id="element-4206">
                                    Crédito (Abono)
                                  </p>
                                  <p className="font-bold text-emerald-900" data-id="element-4207">
                                    {formatCurrency(credito)}
                                  </p>
                                </div>}
                              {descuento > 0 && <div data-id="element-4208">
                                  <p className="text-emerald-600" data-id="element-4209">
                                    Descuento P.P.
                                  </p>
                                  <p className="font-bold text-amber-600" data-id="element-4210">
                                    -{formatCurrency(descuento)}
                                  </p>
                                </div>}
                              <div data-id="element-4211">
                                <p className="text-emerald-600" data-id="element-4212">Factura</p>
                                <p className="font-bold text-emerald-900" data-id="element-4213">
                                  {selectedVisit.cobro.numeroFactura || '-'}
                                </p>
                              </div>
                              <div data-id="element-4214">
                                <p className="text-emerald-600" data-id="element-4215">Estado</p>
                                <p className="font-bold text-emerald-900" data-id="element-4216">
                                  {getInvoiceStatusLabel(selectedVisit.cobro.estadoFactura)}
                                </p>
                              </div>
                            </div>
                            {/* Detalle por factura */}
                            {facturasDetalle.length > 0 && <div className="mt-2" data-id="element-4217">
                                <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase tracking-wide" data-id="element-4218">
                                  Detalle por Factura
                                </p>
                                <div className="space-y-1.5" data-id="element-4219">
                                  {facturasDetalle.map((fd: any, idx: number) => <div key={idx} className="flex items-center justify-between text-xs bg-white/70 rounded-lg px-3 py-2 border border-emerald-100" data-id="element-4220">
                                        <span className="font-semibold text-slate-700" data-id="element-4221">
                                          {fd.numeroFactura}
                                        </span>
                                        <div className="flex items-center gap-3" data-id="element-4222">
                                          {fd.descuento > 0 && <span className="text-amber-600" data-id="element-4223">
                                              PP: -
                                              {formatCurrency(fd.descuento)}
                                            </span>}
                                          <span className="font-bold text-emerald-700" data-id="element-4224">
                                            {formatCurrency(fd.efectivoPagado || fd.abono || 0)}
                                          </span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${fd.saldoNuevo <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-4225">
                                            {fd.saldoNuevo <= 0 ? 'Pagada' : `Saldo: ${formatCurrency(fd.saldoNuevo)}`}
                                          </span>
                                        </div>
                                      </div>)}
                                </div>
                              </div>}
                            {selectedVisit.cobro.metodoPago === 'cheque' && selectedVisit.cobro.chequeImagen && <div className="mt-3" data-id="element-4226">
                                  <p className="text-sm font-semibold text-emerald-700 mb-2" data-id="element-4227">
                                    Imagen del Cheque
                                  </p>
                                  <img src={selectedVisit.cobro.chequeImagen} alt="Cheque adjunto" className="w-full max-h-48 object-contain rounded-xl border border-emerald-200 bg-white" data-id="element-4228" />
                                </div>}
                          </div> : <div className="text-sm text-rose-700" data-id="element-4229">
                            <p data-id="element-4230">No se realizó cobro en esta visita</p>
                            {selectedVisit.motivoNoPago && <p className="mt-2 p-2 bg-rose-100 rounded-lg text-rose-800" data-id="element-4231">
                                <strong data-id="element-4232">Motivo:</strong>{' '}
                                {selectedVisit.motivoNoPago}
                              </p>}
                          </div>}
                      </div>;
            })()}

                {(selectedVisit.tipoVisita === 'venta' || selectedVisit.tipoVisita === 'cobro_y_venta') && <div className={`p-4 rounded-xl border ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'bg-[#eef2fc] border-[#d5e0f7]' : 'bg-amber-50 border-amber-200'}`} data-id="element-4233">
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'text-[#1E3D96]' : 'text-amber-800'}`} data-id="element-4234">
                      <ShoppingCartIcon className="w-5 h-5" data-id="element-4235" />
                      {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'Venta Realizada' : 'Sin Venta'}
                    </h4>
                    {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-4236">
                        <div data-id="element-4237">
                          <p className="text-[#2952C4]" data-id="element-4238">Monto de Venta</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-4239">
                            {formatCurrency(selectedVisit.venta.montoVenta)}
                          </p>
                        </div>
                        <div data-id="element-4240">
                          <p className="text-[#2952C4]" data-id="element-4241">Número de Pedido</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-4242">
                            {selectedVisit.venta.numeroPedido || '-'}
                          </p>
                        </div>
                      </div> : <p className="text-sm text-amber-700 font-medium" data-id="element-4243">
                        No se realizó venta en esta visita
                      </p>}
                  </div>}

                {selectedVisit.comentarios && <div data-id="element-4244">
                    <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-4245">
                      Comentarios
                    </p>
                    <p className="text-slate-600 bg-slate-50 p-3 rounded-xl text-sm" data-id="element-4246">
                      {selectedVisit.comentarios}
                    </p>
                  </div>}

                {selectedVisit.productosVencidos !== null && selectedVisit.productosVencidos !== undefined && <div className={`p-4 rounded-xl border flex items-center gap-3 ${selectedVisit.productosVencidos ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-id="element-4247">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedVisit.productosVencidos ? 'bg-rose-100' : 'bg-emerald-100'}`} data-id="element-4248">
                        <span className="text-base" data-id="element-4249">
                          {selectedVisit.productosVencidos ? '⚠️' : '✅'}
                        </span>
                      </div>
                      <div data-id="element-4250">
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${selectedVisit.productosVencidos ? 'text-rose-500' : 'text-emerald-500'}`} data-id="element-4251">
                          Productos Vencidos
                        </p>
                        <p className={`font-bold text-sm ${selectedVisit.productosVencidos ? 'text-rose-800' : 'text-emerald-800'}`} data-id="element-4252">
                          {selectedVisit.productosVencidos ? 'El cliente tiene productos vencidos' : 'Sin productos vencidos'}
                        </p>
                      </div>
                    </div>}

                {/* Devolucion Items */}
                {selectedVisit.devolucionItems && selectedVisit.devolucionItems.length > 0 && <div className="p-4 rounded-xl border border-rose-200 bg-rose-50" data-id="element-4253">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-rose-800" data-id="element-4254">
                        <PackageIcon className="w-5 h-5" data-id="element-4255" />
                        Productos para Devolución (
                        {selectedVisit.devolucionItems.length})
                      </h4>
                      <div className="space-y-2" data-id="element-4256">
                        {selectedVisit.devolucionItems.map((item, idx) => <div key={idx} className="p-3 bg-white rounded-lg border border-rose-100" data-id="element-4257">
                            <div className="flex items-start justify-between gap-2 mb-2" data-id="element-4258">
                              <div data-id="element-4259">
                                <p className="font-semibold text-slate-900 text-sm" data-id="element-4260">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-4261">
                                  {item.productoSku}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-sm" data-id="element-4262">
                              <div data-id="element-4263">
                                <p className="text-rose-600 text-xs" data-id="element-4264">
                                  Cantidad
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-4265">
                                  {item.cantidad}
                                </p>
                              </div>
                              <div data-id="element-4266">
                                <p className="text-rose-600 text-xs" data-id="element-4267">Precio</p>
                                <p className="font-bold text-slate-900" data-id="element-4268">
                                  {item.precioUnitario ? formatCurrency(item.precioUnitario) : '-'}
                                </p>
                              </div>
                              <div data-id="element-4269">
                                <p className="text-rose-600 text-xs" data-id="element-4270">Lote</p>
                                <p className="font-bold text-slate-900" data-id="element-4271">
                                  {item.lote || '-'}
                                </p>
                              </div>
                              <div data-id="element-4272">
                                <p className="text-rose-600 text-xs" data-id="element-4273">
                                  F. Vencimiento
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-4274">
                                  {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {selectedVisit.proximaVisita && <div className="p-3 bg-amber-50 rounded-xl border border-amber-200" data-id="element-4275">
                    <p className="text-sm text-amber-700" data-id="element-4276">
                      <span className="font-semibold" data-id="element-4277">Próxima visita:</span>{' '}
                      {formatDate(selectedVisit.proximaVisita)}
                    </p>
                  </div>}
              </div>
            </ModalBody>
            <ModalFooter data-id="element-4278">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-4279">
                Cerrar
              </Button>
            </ModalFooter>
          </>}
      </Modal>
    </div>;
}